import { LightningElement, api, wire, track } from "lwc";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import type { PointsData } from "./types";
import * as locales from "./locales";
import { localizeNumber } from "@devforce/trailhead-client-locale";
import { I18n } from "@devforce/trailhead-client-locale";
import {
  createVisibilityObserver,
  disconnectObserver,
} from "common/visibilityHelper";
import { trackUserActivity, type UserActivityEvents } from "common/track";

export const pointsMilestones = [
  1000, 5000, 10000, 15000, 25000, 30000, 40000, 75000, 125000, 175000, 225000,
  275000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000, 1250000,
];

const i18n = new I18n({ pointsCelebrationPage: locales });

export default class PointsCelebrationPage extends LightningElement {
  @api pointsData: PointsData | null = null;
  @track hasStarted = false;

  private milestoneLabelText = "";
  private showCelebration = false;
  private points = 0;
  private visibilityObserver: IntersectionObserver | null = null;
  private visible = false;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  renderedCallback() {
    this.checkMilestone();
    this.setupVisibility();
  }

  disconnectedCallback() {
    disconnectObserver(this.visibilityObserver);
  }

  private get labelOptions() {
    const points = localizeNumber(this.points);
    const milestoneLabel = this.milestoneLabelText;

    return {
      title: {
        points,
      },
      subtitle: {
        milestone_label: milestoneLabel,
      },
    };
  }

  private setupVisibility() {
    if (!this.visibilityObserver && !this.visible) {
      const handleVisible = () => {
        this.visible = true;
        this.track("celebration_page_viewed");
      };

      this.visibilityObserver = createVisibilityObserver(
        this.template.host,
        handleVisible,
        {}
      );
    }
  }

  private checkMilestone() {
    let currentPoints = this.pointsData?.points;
    let newLabelText = "";
    let isMilestone = false;

    // API returns "points__<milestone>", so this converts in case we consume
    // the response string directly. Otherwise, it's parsed in a
    // higher-level component (i.e. React in th-web).
    if (typeof currentPoints === "string") {
      currentPoints = parseInt(currentPoints.replace("points__", ""));
    }

    if (typeof currentPoints === "number" && currentPoints > 0) {
      if (pointsMilestones.includes(currentPoints)) {
        this.points = currentPoints;
        isMilestone = true;
        newLabelText = i18n.t(
          "pointsCelebrationPage",
          `milestone_${currentPoints}_label`
        );
      }
    }

    this.showCelebration = isMilestone;
    this.milestoneLabelText = newLabelText;
  }

  private track(eventName: keyof UserActivityEvents) {
    trackUserActivity(this, eventName, {
      pageLocation: "th-points-celebration-page",
      type: "points",
      milestone: this.points.toString(),
    });
  }
}
